/**
 * MFA Auto-Submit Hook
 *
 * Fires the onSubmit callback automatically when the user enters
 * the expected number of digits, with a configurable debounce delay
 * so the user can visually confirm their input before submission.
 *
 * @module @puffin-core/mfa-ui/hooks/useMfaAutoSubmit
 */

import { useCallback, useEffect, useRef } from 'react';

/**
 * Auto-submit when code reaches the expected length.
 *
 * @param {Object} options
 * @param {string} options.code - Current code value
 * @param {number} options.codeLength - Expected number of characters
 * @param {boolean} options.isLoading - Whether a submission is in progress
 * @param {boolean} options.enabled - Whether auto-submit is active
 * @param {() => void} options.onSubmit - Callback to fire on auto-submit
 * @param {number} [options.delay=300] - Debounce delay in ms
 * @returns {{ disarm: () => void }} `disarm()` records the CURRENT code as submitted and cancels a
 *   pending auto-submit. A caller that submits a code by another path (Enter, the host's button)
 *   calls it, so the timer never submits that code a second time — neither inside the debounce
 *   window nor on the `isLoading` true→false edge after the attempt is refused.
 */
export function useMfaAutoSubmit({ code, codeLength, isLoading, enabled, onSubmit, delay = 300 }) {
  const timerRef = useRef(null);
  // MFA-LOOP-001: the exact code value most recently auto-submitted. A rejected
  // verify cycles the external isLoading true→false while the (still full-length)
  // code is unchanged; that edge re-runs this effect. Without a guard it would
  // re-schedule onSubmit for the same wrong code indefinitely. We therefore
  // auto-submit a given code value at most once — the user must change the code
  // (clear + retype the next rotating TOTP) to re-arm auto-submit. Manual submit
  // (Enter / Verify button) is unaffected.
  const lastSubmittedRef = useRef(null);

  useEffect(() => {
    // Cleanup from the previous effect run always clears timerRef.current,
    // so we never enter a new run with a pending timer.
    if (!enabled || isLoading || !code || code.length !== codeLength) {
      return undefined;
    }

    // Already auto-submitted this exact code — do not re-fire on a re-render
    // (e.g. the post-failure isLoading true→false edge).
    if (code === lastSubmittedRef.current) {
      return undefined;
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastSubmittedRef.current = code;
      onSubmit();
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [code, codeLength, isLoading, enabled, onSubmit, delay]);

  const disarm = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    lastSubmittedRef.current = code;
  }, [code]);

  return { disarm };
}
